// Bin colour helpers. Brand hex is used for graphical fills (dots/bars);
// the *-text variants meet WCAG 2.1 AA (≥ 4.5:1) on a white background.
import type { BandLevel, BinColor } from '@/types';

export const BIN_FILL: Record<BinColor, string> = {
  GREEN: 'var(--bin-green)',
  PURPLE: 'var(--bin-purple)',
  BLUE: 'var(--bin-blue)',
  ORANGE: 'var(--bin-orange)',
  PINK: 'var(--bin-pink)',
};


export const BIN_LABEL: Record<BinColor, string> = {
  GREEN: 'Green',
  PURPLE: 'Purple',
  BLUE: 'Blue',
  ORANGE: 'Orange',
  PINK: 'Pink',
};

// Encouraging one-liners shown on reveal. Never punitive.
export const BIN_BLURB: Record<BinColor, string> = {
  GREEN: 'Mastered — quick and correct!',
  PURPLE: 'Correct! A little more speed next time.',
  BLUE: 'Got it on the second try.',
  ORANGE: 'Solved with full support — progress!',
  PINK: "We'll get there. Your scaffold path is ready.",
};

// Student-facing column order (aspirational: Green first → Pink last).
export const BIN_ORDER: BinColor[] = ['GREEN', 'PURPLE', 'BLUE', 'ORANGE', 'PINK'];

// Short state tag per bin — shared by MasteryBoard's row note and the concept
// tile grid so the two surfaces never drift apart. These name the attempt
// ladder the bins actually encode.
export const BIN_NOTE: Record<BinColor, string> = {
  GREEN: 'first attempt',
  PURPLE: 'mastered, slower',
  BLUE: 'second attempt',
  ORANGE: 'third attempt',
  PINK: 'not solved yet',
};

// Lowercase key matching the --{key}-bg / --{key}-text / --{key}-border CSS
// custom property names in styles/tokens.css.
export const BIN_VAR_KEY: Record<BinColor, string> = {
  GREEN: 'green',
  PURPLE: 'purple',
  BLUE: 'blue',
  ORANGE: 'orange',
  PINK: 'pink',
};

/**
 * The five stops of a bin's ramp, for a dynamically-chosen bin.
 *
 * Prefer the `bin-{colour}-*` Tailwind utilities where the bin is known at build
 * time; this exists for the components that pick one at runtime. Either way the
 * value is a `var()`, so it re-points with the theme.
 *
 * Pair stops, do not mix them with fixed colours. `fill` + `deep` and `bg` +
 * `text` are the two pairings designed to hold contrast, and they hold it in
 * BOTH themes because the two stops invert together. A literal (`text-white`,
 * `bg-pink-50`) on one of these does not, which is how a reveal that read fine
 * in light mode became unreadable on a dark canvas.
 */
export function binRamp(bin: BinColor): {
  bg: string;
  fill: string;
  border: string;
  text: string;
  deep: string;
} {
  const key = BIN_VAR_KEY[bin];
  return {
    bg: `var(--${key}-bg)`,
    fill: `var(--${key}-fill)`,
    border: `var(--${key}-border)`,
    text: `var(--${key}-text)`,
    deep: `var(--${key}-deep)`,
  };
}

// ── Confidence-band helpers ──
export const BAND_LABEL: Record<BandLevel, string> = {
  GREEN: 'Green',
  BLUE: 'Blue',
  ORANGE_PINK: 'Orange-Pink',
};

// Numeric levels for the timeline chart (Y-axis): green=3, blue=2, orange-pink=1.
export const BAND_LEVEL: Record<BandLevel, number> = {
  GREEN: 3,
  BLUE: 2,
  ORANGE_PINK: 1,
};

export const BAND_FILL: Record<BandLevel, string> = {
  GREEN: 'var(--bin-green)',
  BLUE: 'var(--bin-blue)',
  ORANGE_PINK: 'var(--bin-orange)',
};

export const BAND_BLURB: Record<BandLevel, string> = {
  GREEN: 'mastered!',
  BLUE: 'getting closer!',
  ORANGE_PINK: 'keep practising',
};
